import { AlertModal, PageWrapper } from "../../../components"
import {
  ContractSectionCards,
  ContractSummary,
  DesktopColumns,
  MobilePager,
} from "../../../domains/Contracts/components"
import {
  doc as fsDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useShareLink, useViewport } from "../../../hooks"

import arrowLeft from "../../../assets/icons/Arrows/arrow-narrow-left.png"
import { db } from "../../../configs/firebase"
import { scrollToNextUnanswered } from "../../../helpers"
import { useAuth } from "../../../contexts/Auth/AuthContext"
import { useDocumentContext } from "../../../contexts/Document/DocumentContext"
import { useTranslation } from "react-i18next"

const MyDocument = () => {
  const { user } = useAuth()
  const { documentId } = useParams()
  const { setDocumentId, document } = useDocumentContext()
  const navigate = useNavigate()
  const { createShare } = useShareLink()
  const { t } = useTranslation()

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMsg, setAlertMsg] = useState("")
  const [alertTitle, setAlertTitle] = useState("")
  const [showSummary, setShowSummary] = useState(false)
  const [sectionStatus, setSectionStatus] = useState({})
  const [page, setPage] = useState(0)
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const pagerRef = useRef(null)

  const { isMobile, viewportWidth, pageWidth } = useViewport(48)

  useEffect(() => {
    if (documentId) setDocumentId(documentId)
  }, [documentId, setDocumentId])

  const analysis = document?.analysis || null
  const normalizedSections = useMemo(() => {
    const docId = document?._id ?? "doc"
    return (analysis?.sections || []).map((s, index) => ({
      ...s,
      id: s.id ?? `${docId}:${index}`,
    }))
  }, [analysis?.sections, document?._id])

  useEffect(() => {
    if (!document || !normalizedSections.length) return

    const understoodIds = new Set(
      (document.understoodSections || []).map((s) => s?.id).filter(Boolean)
    )
    const unclearIds = new Set(
      (document.unclearSections || []).map((s) => s?.id).filter(Boolean)
    )

    const next = {}
    normalizedSections.forEach((s, idx) => {
      if (understoodIds.has(s.id)) next[idx] = "understood"
      else if (unclearIds.has(s.id)) next[idx] = "notUnderstood"
    })
    setSectionStatus(next)
  }, [
    document?.understoodSections,
    document?.unclearSections,
    normalizedSections,
  ])

  const totalSections = normalizedSections.length
  const completedSections = Object.values(sectionStatus).filter(
    (v) => v === "understood" || v === "notUnderstood"
  ).length
  const progressPercentage =
    totalSections > 0
      ? Math.round((completedSections / totalSections) * 100)
      : 0

  const handleSectionStatusChange = async (sectionIndex, status) => {
    let updatedStatus
    setSectionStatus((prev) => {
      const next = { ...prev }
      if (status == null) delete next[sectionIndex]
      else next[sectionIndex] = status
      updatedStatus = next
      return next
    })

    const section = normalizedSections[sectionIndex]
    const persistDocId = document?._id
    if (section && persistDocId) {
      try {
        const ref = fsDoc(db, "documents", persistDocId)
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref)
          const data = snap.data() || {}
          const understoodArr = Array.isArray(data.understoodSections)
            ? data.understoodSections
            : []
          const unclearArr = Array.isArray(data.unclearSections)
            ? data.unclearSections
            : []

          const filterOut = (arr) =>
            (arr || []).filter((s) => s && s.id !== section.id)
          let nextUnderstood = filterOut(understoodArr)
          let nextUnclear = filterOut(unclearArr)

          if (status === "understood")
            nextUnderstood = [...nextUnderstood, section]
          else if (status === "notUnderstood")
            nextUnclear = [...nextUnclear, section]

          const answeredCount = nextUnderstood.length + nextUnclear.length
          const nextProgress =
            totalSections > 0 ? answeredCount / totalSections : 0

          tx.update(ref, {
            understoodSections: nextUnderstood,
            unclearSections: nextUnclear,
            reviewProgress: nextProgress,
            _updatedAt: serverTimestamp(),
          })
        })
      } catch (e) {
        console.error("Failed to persist section response", e)
      }
    }

    if (status !== null) {
      setTimeout(() => {
        scrollToNextUnanswered({
          currentIndex: sectionIndex,
          sectionStatus: updatedStatus || sectionStatus,
          totalSections,
          setActiveSectionIndex,
        })
      }, 300)
    }
  }

  const handleShowSummary = () => setShowSummary(true)

  const onShare = async (durationHours) => {
    try {
      const shareUrl = await createShare({
        userId: user.uid,
        documentId: document?._id,
        durationHours,
      })
      try {
        await navigator.clipboard.writeText(shareUrl)
      } catch {
        const textArea = document.createElement("textarea")
        textArea.value = shareUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
      }
      setAlertTitle(t("common.linkCopied"))
      setAlertMsg(shareUrl)
      setAlertOpen(true)
    } catch (error) {
      console.error("Error creating share link:", error)
    }
  }

  const handleHorizontalScroll = (e) => {
    const x = e.currentTarget.scrollLeft
    const newPage = Math.round(x / pageWidth)
    if (newPage !== page) setPage(newPage)
  }

  const renderLeftPane = () =>
    !showSummary ? (
      <ContractSectionCards
        sections={normalizedSections}
        sectionStatus={sectionStatus}
        onSectionStatusChange={handleSectionStatusChange}
        onShowSummary={handleShowSummary}
        progressPercentage={progressPercentage}
        activeSectionIndex={activeSectionIndex}
        onActiveSectionChange={setActiveSectionIndex}
      />
    ) : (
      <ContractSummary
        setShowSummary={setShowSummary}
        summary={analysis?.summary}
        sections={analysis?.sections}
        sectionStatus={sectionStatus}
        onShare={onShare}
      />
    )

  const leftPane = useMemo(
    () => renderLeftPane(),
    [
      showSummary,
      analysis,
      sectionStatus,
      progressPercentage,
      activeSectionIndex,
    ]
  )

  const titleOffset = isMobile ? "mb-0" : "mb-6"

  return (
    <PageWrapper
      header={
        <header
          className={`w-full mx-auto ${titleOffset} px-4 md:px-6 lg:px-6 flex items-center justify-between`}
        >
          <div className="flex-none">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="w-10 h-10 rounded-full flex items-center justify-center transition"
            >
              <img src={arrowLeft} alt="back" className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 text-center">
            <span className="text-[28px] font-bold tracking-[0.02em] text-dark-default">
              aigree
            </span>
          </div>
          <div aria-hidden className="flex-none w-10 h-10" />
        </header>
      }
      variant="full"
      background="bg-light-default"
      className="px-0 h-screen overflow-hidden"
    >
      <div className="w-full h-full ">
        {isMobile ? (
          <MobilePager
            leftPane={leftPane}
            pagerRef={pagerRef}
            page={page}
            pageWidth={pageWidth}
            viewportWidth={viewportWidth}
            document={document}
            onCloseDocument={() => navigate(-1)}
            handleHorizontalScroll={handleHorizontalScroll}
          />
        ) : (
          <DesktopColumns
            leftPane={leftPane}
            document={document}
            onCloseDocument={() => navigate(-1)}
          />
        )}
      </div>

      <AlertModal
        open={alertOpen}
        title={alertTitle}
        message={alertMsg}
        onClose={() => setAlertOpen(false)}
      />
    </PageWrapper>
  )
}

export default MyDocument
